/**
 * Pure helpers for the V2 individual dependent-exclusion form.
 *
 * The form loads the full active-coverages tree once (one HTTP call)
 * and filters it locally for each cascading step. The V2 cascade is:
 *
 *   Company -> Insurance -> Policy -> Group -> Employee Coverage -> Dependent
 *
 * See CONTEXT.md ("Cascading Filter Hierarchy") for the domain model.
 *
 * Everything exported from this file is pure: no Vue refs, no
 * `useStore`, no network calls. That makes the filtering rules
 * trivially unit-testable and keeps the SFC focused on UI wiring.
 */

/**
 * Normalise a tree payload so the rest of the helpers can assume the
 * canonical `{ coverages: [...] }` shape.
 *
 * @param {object|null|undefined} tree
 * @returns {{ coverages: Array }}
 */
const ensureTree = (tree) => ({
  coverages: Array.isArray(tree?.coverages) ? tree.coverages : []
});

/**
 * Normalise a raw V2 backend response into the canonical
 * `{ coverages: [...] }` tree shape the rest of the helpers expect.
 *
 * The backend returns a nested Company → Insurance → Policy → Group →
 * Coverage tree as a top-level array:
 *
 *   [{ companyId, companyName, insurances: [{ insuranceCompanyId,
 *     insuranceCompanyName, policies: [{ policyId, policyNumber,
 *     groups: [{ groupId, groupName, coverages: [{ employeeCoverageId,
 *     coverageName, allowDependents, dependents: [...] }] }] }] }] }]
 *
 * This function flattens it to the canonical shape consumers expect:
 *
 *   { coverages: [{ id: employeeCoverageId,
 *     coverage: { id: employeeCoverageId, name: coverageName },
 *     allowDependents, dependents: [{ id: dependentId, rut, fullName }],
 *     policy: { id, numberPolicy, company: { id, businessName },
 *       insuranceCompany: { id, businessName },
 *       groupCompany: { id, name } } }] }
 *
 * Idempotent when the payload is already in canonical shape.
 *
 * @param {object|Array|null|undefined} payload  raw backend response
 * @returns {{ coverages: Array }}
 */
export const normalizeFromBackend = (payload) => {
  // Already canonical — pass through (idempotent).
  if (
    payload &&
    typeof payload === 'object' &&
    !Array.isArray(payload) &&
    Array.isArray(payload.coverages)
  ) {
    return { coverages: payload.coverages };
  }

  const arr = Array.isArray(payload) ? payload : [];
  if (arr.length === 0) return { coverages: [] };

  const coverages = [];

  for (const company of arr) {
    const companyId = company?.companyId;
    const companyName = company?.companyName || `Empresa ${companyId}`;

    for (const insurance of company?.insurances || []) {
      const insuranceId = insurance?.insuranceCompanyId;
      const insuranceName =
        insurance?.insuranceCompanyName || `Aseguradora ${insuranceId}`;

      for (const policy of insurance?.policies || []) {
        const policyId = policy?.policyId;
        const policyNumber = policy?.policyNumber || `Póliza ${policyId}`;

        for (const group of policy?.groups || []) {
          const groupId = group?.groupId;
          const groupName = group?.groupName || `Grupo ${groupId}`;

          for (const cov of group?.coverages || []) {
            const empCovId = cov?.employeeCoverageId;
            coverages.push({
              id: empCovId,
              coverage: { id: empCovId, name: cov?.coverageName },
              allowDependents: cov?.allowDependents,
              dependents: Array.isArray(cov?.dependents)
                ? cov.dependents.map((d) => ({
                    id: d.dependentId,
                    rut: d.rut,
                    fullName: d.fullName
                  }))
                : [],
              policy: {
                id: policyId,
                numberPolicy: policyNumber,
                insuranceBrokerId: policy?.insuranceBrokerId,
                company: { id: companyId, businessName: companyName },
                insuranceCompany: {
                  id: insuranceId,
                  businessName: insuranceName
                },
                groupCompany: { id: groupId, name: groupName }
              }
            });
          }
        }
      }
    }
  }

  return { coverages };
};

/**
 * Tree is empty (no employee coverages at all).
 *
 * @param {object} tree
 * @returns {boolean}
 */
export const isTreeEmpty = (tree) => ensureTree(tree).coverages.length === 0;

/**
 * Tree has employee coverages but none of them have any dependent to
 * exclude. The UI must surface this as a RUT validation error per the
 * acceptance criteria.
 *
 * @param {object} tree
 * @returns {boolean}
 */
export const treeHasNoDependents = (tree) => {
  const coverages = ensureTree(tree).coverages;
  if (coverages.length === 0) return true;
  return coverages.every(
    (c) => !Array.isArray(c.dependents) || c.dependents.length === 0
  );
};

/**
 * Filter an employee coverage's dependents to "active" only.
 *
 * A dependent with no status field is treated as active — the V2
 * endpoint is supposed to return only active coverages with their
 * active dependents, so a missing status means the backend already
 * filtered it.
 *
 * @param {object} coverage
 * @returns {Array}
 */
export const getActiveDependents = (coverage) => {
  if (!Array.isArray(coverage?.dependents)) return [];
  return coverage.dependents.filter(
    (d) => !d?.status || d.status === 'ACTIVE' || d.status === 'active'
  );
};

const uniqueById = (items) => {
  const seen = new Set();
  const out = [];
  for (const item of items) {
    if (!item || item.id === undefined || item.id === null) continue;
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  return out;
};

const sortByLabel = (a, b) =>
  String(a.label || '').localeCompare(String(b.label || ''));

/**
 * Pull every distinct company out of the tree.
 *
 * @param {object} tree
 * @returns {Array<{value,label}>}
 */
export const extractCompanies = (tree) => {
  const coverages = ensureTree(tree).coverages;
  const companies = coverages
    .map((c) => c?.policy?.company)
    .filter(Boolean)
    .map((c) => ({
      id: c.id,
      businessName: c.businessName || c.name || `Empresa ${c.id}`
    }));
  return uniqueById(companies)
    .map((c) => ({ value: c.id, label: c.businessName }))
    .sort(sortByLabel);
};

/**
 * Pull distinct insurance companies for the selected company.
 *
 * @param {object} tree
 * @param {number|string|null} companyId
 * @returns {Array<{value,label}>}
 */
export const extractInsurances = (tree, companyId) => {
  if (companyId === null || companyId === undefined || companyId === '') {
    return [];
  }
  const coverages = ensureTree(tree).coverages;
  const insurances = coverages
    .filter((c) => Number(c?.policy?.company?.id) === Number(companyId))
    .map((c) => c?.policy?.insuranceCompany)
    .filter(Boolean)
    .map((i) => ({
      id: i.id,
      businessName: i.businessName || i.name || `Aseguradora ${i.id}`
    }));
  return uniqueById(insurances)
    .map((i) => ({ value: i.id, label: i.businessName }))
    .sort(sortByLabel);
};

/**
 * Pull distinct policies for the selected company + insurance.
 *
 * @param {object} tree
 * @param {{companyId:any, insuranceId:any}} selection
 * @returns {Array<{value,label}>}
 */
export const extractPolicies = (tree, { companyId, insuranceId } = {}) => {
  if (!companyId || !insuranceId) return [];
  const coverages = ensureTree(tree).coverages;
  const policies = coverages
    .filter(
      (c) =>
        Number(c?.policy?.company?.id) === Number(companyId) &&
        Number(c?.policy?.insuranceCompany?.id) === Number(insuranceId)
    )
    .map((c) => ({
      id: c.policy.id,
      numberPolicy: c.policy.numberPolicy || `Póliza ${c.policy.id}`
    }));
  return uniqueById(policies)
    .map((p) => ({ value: p.id, label: p.numberPolicy }))
    .sort(sortByLabel);
};

/**
 * Filter the tree to only the coverages whose policy is selected.
 *
 * @param {object} tree
 * @param {Array<number|string>} policyIds
 * @returns {Array}
 */
export const filterCoveragesByPolicies = (tree, policyIds) => {
  if (!Array.isArray(policyIds) || policyIds.length === 0) return [];
  const ids = new Set(policyIds.map((id) => Number(id)));
  return ensureTree(tree).coverages.filter((c) =>
    ids.has(Number(c?.policy?.id))
  );
};

/**
 * Group coverages by policy -> groupCompany, preserving original
 * order. Used to render the dependent checkboxes nested under each
 * selected policy + group.
 *
 * Output shape:
 *   [
 *     {
 *       policyId, policyNumber,
 *       groups: [
 *         {
 *           groupCompanyId, groupCompanyName,
 *           coverages: [ ...originalCoverageObjects ]
 *         }
 *       ]
 *     }
 *   ]
 *
 * @param {Array} coverages
 */
export const groupCoveragesByPolicyAndGroup = (coverages) => {
  if (!Array.isArray(coverages)) return [];
  const policies = [];
  const policyIndex = new Map();

  coverages.forEach((coverage) => {
    const policy = coverage?.policy;
    if (!policy) return;
    const policyId = policy.id;
    if (!policyIndex.has(policyId)) {
      const entry = {
        policyId,
        policyNumber: policy.numberPolicy || `Póliza ${policyId}`,
        groups: []
      };
      const groupIndex = new Map();
      policyIndex.set(policyId, { entry, groupIndex });
      policies.push(entry);
    }
    const { entry, groupIndex } = policyIndex.get(policyId);

    const group = policy.groupCompany;
    const groupId = group?.id ?? null;
    if (!groupIndex.has(groupId)) {
      const groupEntry = {
        groupCompanyId: groupId,
        groupCompanyName:
          group?.name || (groupId == null ? 'Sin grupo' : `Grupo ${groupId}`),
        coverages: []
      };
      groupIndex.set(groupId, groupEntry);
      entry.groups.push(groupEntry);
    }
    groupIndex.get(groupId).coverages.push(coverage);
  });

  return policies;
};

/**
 * Flatten a { employeeCoverageId -> Set<dependentId> } map into the
 * list of `{ dependentId, employeeCoverageId }` items the V2 POST
 * expects. One checkbox = one item.
 *
 * @param {Record<string, Set<number|string>>|Record<string, Array<number|string>>} selectedDependents
 * @returns {Array<{dependentId:number, employeeCoverageId:number}>}
 */
export const buildExclusionItems = (selectedDependents) => {
  if (!selectedDependents) return [];
  const out = [];
  for (const [coverageId, depIds] of Object.entries(selectedDependents)) {
    const ids =
      depIds instanceof Set
        ? Array.from(depIds)
        : Array.isArray(depIds)
          ? depIds
          : [];
    ids.forEach((dependentId) => {
      out.push({
        dependentId: Number(dependentId),
        employeeCoverageId: Number(coverageId)
      });
    });
  }
  return out;
};

/**
 * Build the final payload for POST /api/v2/tickets/dependents-exclusions.
 *
 * Backend schema requires:
 *   companyId, brokerId, insuranceCompanyId, rut, endDate, policies,
 *   dependentExclusions (each with dependentId, employeeCoverageId).
 *   message is optional (allowed empty/null).
 *
 * @param {{
 *   endDate: string,
 *   items: Array<{dependentId:number, employeeCoverageId:number}>,
 *   companyId: number,
 *   brokerId: number,
 *   insuranceCompanyId: number,
 *   rut: string,
 *   message: string,
 *   policies: Array<number>
 * }} input
 * @returns {{ endDate:string, dependentExclusions:Array, companyId:number, brokerId:number, insuranceCompanyId:number, rut:string, message:string, policies:Array<number> }}
 */
export const buildExclusionPayload = ({
  endDate,
  items,
  companyId,
  brokerId,
  insuranceCompanyId,
  rut,
  message,
  policies
} = {}) => {
  const itemsArr = Array.isArray(items) ? items : [];
  if (itemsArr.length === 0) {
    throw new Error('Debe seleccionar al menos una carga a dar de baja.');
  }
  if (!endDate) {
    throw new Error('Debe seleccionar una fecha de término.');
  }
  if (companyId == null) {
    throw new Error('Debe seleccionar una empresa.');
  }
  if (brokerId == null) {
    throw new Error('No se pudo identificar al corredor.');
  }
  if (insuranceCompanyId == null) {
    throw new Error('Debe seleccionar una aseguradora.');
  }
  if (!rut) {
    throw new Error('Debe ingresar un RUT.');
  }
  if (!Array.isArray(policies) || policies.length === 0) {
    throw new Error('Debe seleccionar al menos una póliza.');
  }
  return {
    endDate,
    dependentExclusions: itemsArr.map((it) => ({
      dependentId: Number(it.dependentId),
      employeeCoverageId: Number(it.employeeCoverageId)
    })),
    companyId,
    brokerId,
    insuranceCompanyId,
    rut,
    message: message || '',
    policies
  };
};
